import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  FiArrowLeft,
  FiUser,
  FiMail,
  FiPhone,
  FiGift,
  FiMapPin,
  FiBriefcase,
  FiEdit2,
  FiLock,
  FiUnlock,
  FiTrash2,
  FiCalendar,
  FiCheck,
  FiX,
  FiSend,
  FiEye,
  FiMousePointer,
  FiAlertCircle,
  FiClock,
  FiFileText,
  FiCopy,
  FiExternalLink,
  FiPlus,
  FiRefreshCw,
  FiSearch,
} from "react-icons/fi";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CardSkeleton } from "@/components/common";
import { Tooltip } from "@/components/ui/tooltip";
import { getLenderLogo, getAllLenderLogos } from "@/config/lender-logos.config";
import { contentToHtml, EMAIL_BRAND } from "@/components/email";

const loanTypeLabels: Record<string, string> = {
  home_purchase: "Home Purchase",
  refinance: "Refinance",
  investment: "Investment",
  first_home: "First Home",
  construction: "Construction",
};

interface CustomerLoan {
  id: string;
  loanType: string | null;
  loanPurpose: string | null;
  loanCategory: string | null;
  loanAmount: number | null;
  lender: string | null;
  settlementDate: string | null;
}

interface CustomerDetail {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  address: string | null;
  emailVerified: boolean;
  isLocked: boolean;
  lastLoginAt: string | null;
  birthday: string | null;
  loanSettlementDate: string | null;
  loanType: string | null;
  preferredName: string | null;
  customerNotes: string | null;
  createdAt: string;
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;
  title: string | null;
  gender: string | null;
  occupation: string | null;
  company: string | null;
  industry: string | null;
  abn: string | null;
  acn: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  postCode: string | null;
  country: string | null;
  homePhone: string | null;
  workPhone: string | null;
  homeFax: string | null;
  workFax: string | null;
  spousePartner: string | null;
  selfEmployed: boolean;
  source: string | null;
  accountStatus: string | null;
  customerType: string | null;
  bestTimeToCall: string | null;
  emailStatus: string | null;
  neverEmail: boolean;
  dontSendMarketing: boolean;
  crmId: string | null;
  crmCreatedAt: string | null;
  productGroup: string | null;
  fixedPeriodMonths: number | null;
  driversLicence: string | null;
  jobTitle: string | null;
  contactMethod: string | null;
  campaign: string | null;
  channel: string | null;
  referenceId: string | null;
  loans: CustomerLoan[];
  emails: CustomerEmail[];
  upcomingEvents: UpcomingEvent[];
  factFinders: CustomerFactFinder[];
}

interface CustomerFactFinder {
  id: string;
  accessToken: string | null;
  status: "draft" | "submitted" | "in_review" | "approved" | "archived";
  completedSections: string[];
  lastSectionSaved: string | null;
  createdAt: string;
  updatedAt: string;
  createdByType: "admin" | "customer" | null;
}

interface UpcomingEvent {
  type: string;
  label: string;
  date: string;
  automationId?: string;
  automationName?: string;
  offsetDays?: number;
  automationSubject?: string;
  automationContent?: string;
}

interface CustomerEmail {
  id: string;
  status: string | null;
  scheduledFor: string | null;
  sentAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  errorMessage: string | null;
  automationName: string | null;
  subject: string | null;
  triggerType: string | null;
}

function formatLoansTable(loans: CustomerLoan[]): string {
  if (!loans || loans.length === 0) return "";
  const hBg = "#00263e", hC = "#ffffff", bC = "#e2e8f0";
  const rows = loans.map((loan, i) => {
    const bg = i % 2 === 0 ? "#ffffff" : "#f8fafc";
    const purpose = loan.loanPurpose || (loan.loanType && loanTypeLabels[loan.loanType]) || "-";
    const amount = loan.loanAmount ? `$${Number(loan.loanAmount).toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : "-";
    const lender = loan.lender || "-";
    let settled = "-";
    if (loan.settlementDate) {
      const d = new Date(loan.settlementDate);
      settled = `${d.toLocaleString("en-AU", { month: "short" })} ${d.getFullYear()}`;
    }
    return `<tr style="background:${bg};"><td style="padding:10px 14px;border-bottom:1px solid ${bC};font-size:14px;">${purpose}</td><td style="padding:10px 14px;border-bottom:1px solid ${bC};font-size:14px;">${lender}</td><td style="padding:10px 14px;border-bottom:1px solid ${bC};font-size:14px;text-align:right;font-weight:600;">${amount}</td><td style="padding:10px 14px;border-bottom:1px solid ${bC};font-size:14px;text-align:right;">${settled}</td></tr>`;
  }).join("");
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:1px solid ${bC};border-radius:8px;overflow:hidden;margin:12px 0;"><tr style="background:${hBg};"><th style="padding:10px 14px;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:${hC};text-align:left;">Purpose</th><th style="padding:10px 14px;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:${hC};text-align:left;">Lender</th><th style="padding:10px 14px;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:${hC};text-align:right;">Amount</th><th style="padding:10px 14px;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:${hC};text-align:right;">Settled</th></tr>${rows}</table>`;
}

function interpolateCustomerVars(template: string, customer: CustomerDetail): string {
  const firstName = customer.preferredName || customer.firstName || customer.name?.split(" ")[0] || "there";
  const years = customer.loanSettlementDate
    ? Math.floor((Date.now() - new Date(customer.loanSettlementDate).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
    : 0;
  const vars: Record<string, string> = {
    "{{name}}": customer.name || "Valued Customer",
    "{{firstName}}": firstName,
    "{{preferredName}}": customer.preferredName || firstName,
    "{{email}}": customer.email,
    "{{loanType}}": customer.loanType || "home loan",
    "{{years}}": String(years),
    "{{loanCount}}": String(customer.loans.length),
    "{{allLoans}}": formatLoansTable(customer.loans),
  };
  let result = template;
  for (const [key, value] of Object.entries(vars)) {
    result = result.replaceAll(key, value);
  }
  // Fix singular/plural: "1-Years" → "1-Year", "1 years" → "1 year"
  result = result.replace(/\b1[- ]Years\b/g, "1-Year");
  result = result.replace(/\b1 years\b/gi, "1 year");
  return result;
}

function InfoItem({ label, value, icon: Icon }: { label: string; value: string | null | undefined; icon?: typeof FiUser }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5 py-2">
      {Icon && <Icon className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <p className="text-[11px] text-slate-400 uppercase tracking-wider">{label}</p>
        <p className="text-sm text-slate-800 break-words">{value}</p>
      </div>
    </div>
  );
}

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lockingId, setLockingId] = useState<string | null>(null);
  const [isCreatingFF, setIsCreatingFF] = useState(false);
  const [isFFConfirmOpen, setIsFFConfirmOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [previewEventIdx, setPreviewEventIdx] = useState<number | null>(null);
  const [isAddLoanOpen, setIsAddLoanOpen] = useState(false);
  const [isAddingLoan, setIsAddingLoan] = useState(false);
  const [loanForm, setLoanForm] = useState({ loanPurpose: "", loanCategory: "", loanType: "", loanAmount: "", lender: "", settlementDate: "" });
  const [lenderSearch, setLenderSearch] = useState("");
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [isLenderDropdownOpen, setIsLenderDropdownOpen] = useState(false);
  const lenderDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isLenderDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (lenderDropdownRef.current && !lenderDropdownRef.current.contains(e.target as Node)) {
        setIsLenderDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isLenderDropdownOpen]);

  const fetchCustomer = useCallback(async () => {
    try {
      const response = await fetch(`/api/admin/customers/${id}`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json() as { data?: CustomerDetail };
        if (data.data) setCustomer(data.data);
      } else {
        toast.error("Customer not found");
        navigate("/admin/customers");
      }
    } catch {
      toast.error("Failed to load customer");
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchCustomer();
  }, [fetchCustomer]);

  const handleSendTestEmail = async (automationId: string) => {
    if (!customer) return;
    setIsSendingTest(true);
    try {
      const response = await fetch(`/api/admin/automations/${automationId}/test`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customerId: customer.id }),
      });
      const data = await response.json() as { message?: string; error?: string };
      if (response.ok) {
        toast.success(data.message || "Test email sent to your admin email");
      } else {
        toast.error(data.error || "Failed to send test email");
      }
    } catch {
      toast.error("Failed to send test email");
    } finally {
      setIsSendingTest(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchCustomer();
    setIsRefreshing(false);
    toast.success("Customer data refreshed");
  };

  const handleToggleLock = async () => {
    if (!customer) return;
    setLockingId(customer.id);
    try {
      const response = await fetch(`/api/admin/customers/${customer.id}/lock`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isLocked: !customer.isLocked }),
      });
      if (response.ok) {
        toast.success(customer.isLocked ? "Customer unlocked" : "Customer locked");
        fetchCustomer();
      }
    } catch {
      toast.error("Failed to update lock status");
    } finally {
      setLockingId(null);
    }
  };

  const handleDelete = async () => {
    if (!customer || !confirm("Are you sure you want to delete this customer?")) return;
    try {
      const response = await fetch(`/api/admin/customers/${customer.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Customer deleted");
        navigate("/admin/customers");
      }
    } catch {
      toast.error("Failed to delete customer");
    }
  };

  const handleCreateFactFinder = async () => {
    if (!customer) return;
    setIsCreatingFF(true);
    try {
      const response = await fetch("/api/admin/fact-finders", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customerId: customer.id, email: customer.email }),
      });
      if (response.ok) {
        toast.success("Fact finder created & invite email sent");
        setIsFFConfirmOpen(false);
        fetchCustomer();
      } else {
        const data = await response.json() as { error?: string };
        toast.error(data.error || "Failed to create fact finder");
      }
    } catch {
      toast.error("Failed to create fact finder");
    } finally {
      setIsCreatingFF(false);
    }
  };

  const copyFactFinderLink = (accessToken: string) => {
    const link = `${window.location.origin}/fact-finder/${accessToken}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard");
  };

  const handleAddLoan = async () => {
    if (!customer) return;
    setIsAddingLoan(true);
    try {
      const response = await fetch(`/api/admin/customers/${customer.id}/loans`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...loanForm,
          loanAmount: loanForm.loanAmount ? parseFloat(loanForm.loanAmount) : undefined,
        }),
      });
      if (response.ok) {
        toast.success("Loan added");
        setIsAddLoanOpen(false);
        setLoanForm({ loanPurpose: "", loanCategory: "", loanType: "", loanAmount: "", lender: "", settlementDate: "" });
        fetchCustomer();
      } else {
        toast.error("Failed to add loan");
      }
    } catch {
      toast.error("Failed to add loan");
    } finally {
      setIsAddingLoan(false);
    }
  };

  const [deleteLoanId, setDeleteLoanId] = useState<string | null>(null);
  const [isDeletingLoan, setIsDeletingLoan] = useState(false);

  const handleDeleteLoan = async () => {
    if (!customer || !deleteLoanId) return;
    setIsDeletingLoan(true);
    try {
      const response = await fetch(`/api/admin/customers/${customer.id}/loans/${deleteLoanId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Loan deleted");
        setDeleteLoanId(null);
        fetchCustomer();
      }
    } catch {
      toast.error("Failed to delete loan");
    } finally {
      setIsDeletingLoan(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <CardSkeleton />
        <div className="grid md:grid-cols-2 gap-6">
          <CardSkeleton />
          <CardSkeleton />
        </div>
      </div>
    );
  }

  if (!customer) return null;

  const fullAddress = [customer.street, customer.city, customer.state, customer.postCode, customer.country]
    .filter(Boolean)
    .join(", ") || customer.address;

  const statusConfig = {
    existing: { label: "Existing (Paying)", className: "bg-green-100 text-green-700" },
    ineligible: { label: "Ineligible", className: "bg-amber-100 text-amber-700" },
    discharged: { label: "Discharged", className: "bg-slate-100 text-slate-600" },
  } as Record<string, { label: string; className: string }>;

  const status = customer.accountStatus && statusConfig[customer.accountStatus];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin/customers")} className="h-8 w-8 p-0 shrink-0">
            <FiArrowLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="font-serif text-xl sm:text-2xl font-medium text-slate-900 truncate">
              {customer.name || "No name"}
              {customer.preferredName && (
                <span className="text-base sm:text-lg text-slate-400 ml-2">"{customer.preferredName}"</span>
              )}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5 truncate">{customer.email}</p>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={isRefreshing} className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600">
              <FiRefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? "animate-spin" : ""}`} />
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/customers", { state: { editCustomerId: customer.id } })} className="h-8 px-2 sm:px-3">
              <FiEdit2 className="w-3.5 h-3.5 sm:mr-1.5" /> <span className="hidden sm:inline">Edit</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggleLock}
              disabled={lockingId === customer.id}
              className={`h-8 w-8 p-0 ${customer.isLocked ? "text-green-700" : "text-red-600"}`}
            >
              {customer.isLocked ? <FiUnlock className="w-4 h-4" /> : <FiLock className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleDelete} className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50">
              <FiTrash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap pl-11">
          {status && (
            <Badge variant="secondary" className={status.className}>{status.label}</Badge>
          )}
          {!customer.accountStatus && (
            <Badge variant="outline" className="text-purple-600 border-purple-200 border-dashed">Unassigned</Badge>
          )}
          {customer.isLocked && (
            <Badge variant="secondary" className="bg-red-100 text-red-700">
              <FiLock className="w-3 h-3 mr-1" /> Locked
            </Badge>
          )}
          {customer.emailVerified && (
            <Badge variant="secondary" className="bg-green-100 text-green-700">
              <FiCheck className="w-3 h-3 mr-1" /> Verified
            </Badge>
          )}
        </div>
      </div>

      {/* Main content - 2 column layout */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left column - 2/3 width */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact & Personal */}
          <Card>
            <CardContent className="pt-5">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Contact & Personal</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6">
                <InfoItem label="Email" value={customer.email} icon={FiMail} />
                <InfoItem label="Mobile" value={customer.phone} icon={FiPhone} />
                <InfoItem label="Home Phone" value={customer.homePhone} icon={FiPhone} />
                <InfoItem label="Work Phone" value={customer.workPhone} icon={FiPhone} />
                <InfoItem label="Birthday" value={customer.birthday} icon={FiGift} />
                <InfoItem label="Gender" value={customer.gender === "M" ? "Male" : customer.gender === "F" ? "Female" : customer.gender} icon={FiUser} />
                <InfoItem label="Spouse / Partner" value={customer.spousePartner} icon={FiUser} />
                <InfoItem label="Best Time to Call" value={customer.bestTimeToCall} icon={FiCalendar} />
                <InfoItem label="Contact Method" value={customer.contactMethod} />
                <InfoItem label="Driver's Licence" value={customer.driversLicence} />
              </div>
              {fullAddress && (
                <div className="mt-2 pt-2 border-t">
                  <InfoItem label="Address" value={fullAddress} icon={FiMapPin} />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Loans */}
          <Card>
            <CardContent className="pt-5">
              <div className="flex items-center justify-between gap-2 mb-3">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider shrink-0">
                  Loans ({customer.loans.length})
                </h3>
                <div className="flex items-center gap-2">
                  {customer.loanSettlementDate && (
                    <span className="text-xs text-slate-500 truncate hidden sm:inline">
                      Latest: {new Date(customer.loanSettlementDate).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setIsAddLoanOpen(true)} className="h-7 text-xs shrink-0">
                    <FiPlus className="w-3 h-3 mr-1" />
                    <span className="hidden sm:inline">Add Loan</span>
                    <span className="sm:hidden">Add</span>
                  </Button>
                </div>
              </div>

              {customer.loans.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No loans on record</p>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="hidden md:block border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
                          <th className="text-left font-semibold px-4 py-2.5">Purpose</th>
                          <th className="text-left font-semibold px-4 py-2.5">Category</th>
                          <th className="text-left font-semibold px-4 py-2.5">Lender</th>
                          <th className="text-right font-semibold px-4 py-2.5">Amount</th>
                          <th className="text-right font-semibold px-4 py-2.5">Settlement</th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {customer.loans.map((loan) => (
                          <tr key={loan.id} className="group hover:bg-slate-50/50">
                            <td className="px-4 py-3">
                              <span className="font-medium text-slate-800">
                                {loan.loanPurpose || (loan.loanType && loanTypeLabels[loan.loanType]) || "-"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-600">{loan.loanCategory || "-"}</td>
                            <td className="px-4 py-3 text-slate-600">
                              {loan.lender ? (
                                <span className="flex items-center gap-2">
                                  {getLenderLogo(loan.lender) && (
                                    <img src={getLenderLogo(loan.lender)!} alt={loan.lender} className="w-5 h-5 object-contain shrink-0" />
                                  )}
                                  {loan.lender}
                                </span>
                              ) : "-"}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-slate-800">
                              {loan.loanAmount ? `$${Number(loan.loanAmount).toLocaleString()}` : "-"}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-600">
                              {loan.settlementDate
                                ? new Date(loan.settlementDate).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
                                : "-"}
                            </td>
                            <td className="px-2 py-3">
                              <button onClick={() => setDeleteLoanId(loan.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 transition-all p-1">
                                <FiTrash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Mobile cards */}
                  <div className="md:hidden space-y-2">
                    {customer.loans.map((loan) => (
                      <div key={loan.id} className="rounded-xl bg-slate-50 p-3 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-slate-800">
                            {loan.loanPurpose || (loan.loanType && loanTypeLabels[loan.loanType]) || "Loan"}
                          </span>
                          <div className="flex items-center gap-2">
                            {loan.loanAmount && (
                              <span className="text-sm font-semibold text-slate-800">
                                ${Number(loan.loanAmount).toLocaleString()}
                              </span>
                            )}
                            <button onClick={() => setDeleteLoanId(loan.id)} className="text-slate-300 hover:text-red-500 transition-colors p-0.5">
                              <FiTrash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <div className="flex items-center gap-2">
                            {loan.lender && getLenderLogo(loan.lender) && (
                              <img src={getLenderLogo(loan.lender)!} alt={loan.lender} className="w-4 h-4 object-contain" />
                            )}
                            <span>{loan.lender || "-"}</span>
                            {loan.loanCategory && (
                              <>
                                <span className="text-slate-300">|</span>
                                <span>{loan.loanCategory}</span>
                              </>
                            )}
                          </div>
                          {loan.settlementDate && (
                            <span>{new Date(loan.settlementDate).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Email Activity */}
          <Card>
            <CardContent className="pt-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider shrink-0">
                  Email Activity ({customer.emails.length})
                </h3>
                {customer.emails.length > 0 && (
                  <div className="flex items-center gap-2 sm:gap-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <FiSend className="w-3 h-3 text-green-500" />
                      {customer.emails.filter(e => e.status === "sent").length} sent
                    </span>
                    <span className="flex items-center gap-1">
                      <FiEye className="w-3 h-3 text-blue-500" />
                      {customer.emails.filter(e => e.openedAt).length} opened
                    </span>
                    <span className="flex items-center gap-1">
                      <FiMousePointer className="w-3 h-3 text-purple-500" />
                      {customer.emails.filter(e => e.clickedAt).length} clicked
                    </span>
                  </div>
                )}
              </div>

              {customer.emails.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No emails sent yet</p>
              ) : (
                <div className="space-y-2">
                  {customer.emails.map((email) => {
                    const statusConfig: Record<string, { icon: typeof FiCheck; color: string; label: string }> = {
                      sent: { icon: FiCheck, color: "text-green-600", label: "Sent" },
                      pending: { icon: FiClock, color: "text-amber-500", label: "Pending" },
                      sending: { icon: FiSend, color: "text-blue-500", label: "Sending" },
                      failed: { icon: FiAlertCircle, color: "text-red-500", label: "Failed" },
                      cancelled: { icon: FiX, color: "text-slate-400", label: "Cancelled" },
                      skipped: { icon: FiX, color: "text-slate-400", label: "Skipped" },
                    };
                    const s = statusConfig[email.status || "pending"] || statusConfig.pending;
                    const StatusIcon = s.icon;

                    return (
                      <div key={email.id} className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
                        <div className={`w-8 h-8 rounded-lg bg-white flex items-center justify-center shrink-0 ${s.color}`}>
                          <StatusIcon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">
                            {email.subject || email.automationName || "Email"}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            {email.automationName && email.subject && (
                              <span>{email.automationName}</span>
                            )}
                            {email.triggerType && (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                {email.triggerType.replace(/_/g, " ")}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className={`text-xs font-medium ${s.color}`}>{s.label}</p>
                          <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-0.5">
                            {email.sentAt ? (
                              <span>{new Date(email.sentAt).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span>
                            ) : email.scheduledFor ? (
                              <span>Scheduled {new Date(email.scheduledFor).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</span>
                            ) : null}
                            {email.openedAt && <FiEye className="w-3 h-3 text-blue-400" title={`Opened ${new Date(email.openedAt).toLocaleString("en-AU")}`} />}
                            {email.clickedAt && <FiMousePointer className="w-3 h-3 text-purple-400" title={`Clicked ${new Date(email.clickedAt).toLocaleString("en-AU")}`} />}
                          </div>
                        </div>
                        {email.errorMessage && (
                          <span className="text-xs text-red-500 truncate max-w-[120px]" title={email.errorMessage}>
                            {email.errorMessage}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Fact Finders */}
          <Card>
            <CardContent className="pt-5">
              <div className="flex items-center justify-between gap-2 mb-3">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider shrink-0">
                  Fact Finders ({customer.factFinders.length})
                </h3>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setIsFFConfirmOpen(true)}
                  className="h-7 text-xs shrink-0"
                >
                  <FiPlus className="w-3 h-3 mr-1" />
                  <span className="hidden sm:inline">Create & Send Invite</span>
                  <span className="sm:hidden">New</span>
                </Button>
              </div>

              {customer.factFinders.length === 0 ? (
                <div className="text-center py-6 bg-slate-50 rounded-xl">
                  <FiFileText className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-muted-foreground">No fact finders yet</p>
                  <p className="text-xs text-slate-400 mt-1">Create one to send the customer a form to fill out</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {customer.factFinders.map((ff) => {
                    const totalSections = 8;
                    const completed = Array.isArray(ff.completedSections) ? ff.completedSections.length : 0;
                    const progress = Math.round((completed / totalSections) * 100);
                    const statusConfig: Record<string, { color: string; bg: string }> = {
                      draft: { color: "text-amber-700", bg: "bg-amber-100" },
                      submitted: { color: "text-blue-700", bg: "bg-blue-100" },
                      in_review: { color: "text-purple-700", bg: "bg-purple-100" },
                      approved: { color: "text-green-700", bg: "bg-green-100" },
                      archived: { color: "text-slate-600", bg: "bg-slate-100" },
                    };
                    const s = statusConfig[ff.status] || statusConfig.draft;

                    return (
                      <div key={ff.id} className="rounded-xl bg-slate-50 px-4 py-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <FiFileText className="w-4 h-4 text-slate-400" />
                            <Badge variant="secondary" className={`text-xs ${s.bg} ${s.color}`}>
                              {ff.status.replace("_", " ")}
                            </Badge>
                            <span className="text-xs text-slate-400">
                              {ff.createdByType === "admin" ? "Created by admin" : "Started by customer"}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            {ff.accessToken && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyFactFinderLink(ff.accessToken!)}
                                  className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600"
                                  title="Copy link"
                                >
                                  <FiCopy className="w-3.5 h-3.5" />
                                </Button>
                                <a
                                  href={`/fact-finder/${ff.accessToken}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center justify-center h-7 w-7 text-slate-400 hover:text-slate-600 transition-colors"
                                  title="Open public link"
                                >
                                  <FiExternalLink className="w-3.5 h-3.5" />
                                </a>
                              </>
                            )}
                            <Link
                              to={`/admin/fact-finders/${ff.id}`}
                              className="inline-flex items-center justify-center h-7 w-7 text-slate-400 hover:text-accent transition-colors"
                              title="View details"
                            >
                              <FiEye className="w-3.5 h-3.5" />
                            </Link>
                          </div>
                        </div>
                        {/* Progress bar */}
                        <div className="flex items-center gap-3">
                          <div className="flex-1 bg-slate-200 rounded-full h-1.5 overflow-hidden">
                            <div
                              className={`h-1.5 rounded-full transition-all ${ff.status === "approved" ? "bg-green-500" : "bg-accent"}`}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-500 shrink-0 w-16 text-right">
                            {completed}/{totalSections} sections
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-2 text-[11px] text-slate-400">
                          <span>Created {new Date(ff.createdAt).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span>
                          <span>Updated {new Date(ff.updatedAt).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Employment */}
          {(customer.occupation || customer.company || customer.industry || customer.jobTitle || customer.abn) && (
            <Card>
              <CardContent className="pt-5">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Employment</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6">
                  <InfoItem label="Occupation" value={customer.occupation} icon={FiBriefcase} />
                  <InfoItem label="Job Title" value={customer.jobTitle} icon={FiBriefcase} />
                  <InfoItem label="Company" value={customer.company} />
                  <InfoItem label="Industry" value={customer.industry} />
                  <InfoItem label="ABN" value={customer.abn} />
                  <InfoItem label="ACN" value={customer.acn} />
                  {customer.selfEmployed && (
                    <div className="flex items-center gap-2 py-2">
                      <Badge variant="secondary" className="text-xs">Self Employed</Badge>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          {customer.customerNotes && (
            <Card>
              <CardContent className="pt-5">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Notes</h3>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{customer.customerNotes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column - sidebar */}
        <div className="space-y-6">
          {/* Upcoming Events */}
          {customer.upcomingEvents.length > 0 && (
            <Card>
              <CardContent className="pt-5 space-y-3">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Upcoming Events</h3>
                {customer.upcomingEvents.map((event, i) => {
                  const eventDate = new Date(event.date);
                  const now = new Date();
                  const daysUntil = Math.ceil((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                  const isWithin30Days = daysUntil <= 30;
                  const iconMap: Record<string, typeof FiGift> = {
                    birthday: FiGift,
                    settlement_anniversary: FiCalendar,
                    christmas: FiGift,
                  };
                  const EventIcon = iconMap[event.type] || FiCalendar;

                  return (
                    <div key={i} className={`rounded-lg flex items-start gap-3 px-3 py-2.5 ${isWithin30Days ? "bg-accent/5 border border-accent/20" : "bg-slate-50"}`}>
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                        event.type === "birthday" ? "bg-pink-100 text-pink-600" :
                        event.type === "christmas" ? "bg-red-100 text-red-600" :
                        "bg-blue-100 text-blue-600"
                      }`}>
                        <EventIcon className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800">{event.label}</p>
                        <p className="text-xs text-slate-500">
                          {eventDate.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
                          <span className={`ml-1.5 ${isWithin30Days ? "text-accent font-medium" : ""}`}>
                            ({daysUntil === 0 ? "Today" : daysUntil === 1 ? "Tomorrow" : `${daysUntil} days`})
                          </span>
                        </p>
                        {event.automationName ? (
                          <Tooltip
                            side="bottom"
                            content={
                              <div className="space-y-1">
                                <p className="font-medium">{event.automationName}</p>
                                <p className="text-xs text-muted-foreground">
                                  Email sends {event.offsetDays ? `${event.offsetDays} day${event.offsetDays !== 1 ? "s" : ""} before` : "on the day"}
                                </p>
                              </div>
                            }
                          >
                            <p className="text-[11px] text-green-600 flex items-center gap-1 mt-0.5 cursor-help">
                              <FiCheck className="w-3 h-3" />
                              {event.automationName}
                              {event.offsetDays ? (
                                <span className="text-green-500/70">({event.offsetDays}d prior)</span>
                              ) : (
                                <span className="text-green-500/70">(on day)</span>
                              )}
                            </p>
                          </Tooltip>
                        ) : (
                          <Tooltip
                            side="bottom"
                            content={
                              <p className="text-xs">No automation is set up for this event. Create one in the Automations page.</p>
                            }
                          >
                            <p className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5 cursor-help">
                              <FiAlertCircle className="w-3 h-3" />
                              No automation
                            </p>
                          </Tooltip>
                        )}
                      </div>
                      {event.automationContent && (
                        <button
                          onClick={() => setPreviewEventIdx(i)}
                          className="shrink-0 mt-1 w-7 h-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-accent hover:border-accent/30 transition-colors"
                          title="Preview email"
                        >
                          <FiEye className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Quick Info */}
          <Card>
            <CardContent className="pt-5 space-y-4">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Account</span>
                  {status ? (
                    <Badge variant="secondary" className={`text-xs ${status.className}`}>{status.label}</Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs text-purple-600 border-purple-200">Unassigned</Badge>
                  )}
                </div>
                {customer.customerType && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Type</span>
                    <span className="text-slate-800 capitalize">{customer.customerType}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Login</span>
                  <span className="text-slate-800">
                    {customer.isLocked ? (
                      <span className="text-red-600 flex items-center gap-1"><FiLock className="w-3 h-3" /> Locked</span>
                    ) : customer.emailVerified ? (
                      <span className="text-green-600 flex items-center gap-1"><FiCheck className="w-3 h-3" /> Verified</span>
                    ) : (
                      <span className="text-slate-400 flex items-center gap-1"><FiX className="w-3 h-3" /> Not verified</span>
                    )}
                  </span>
                </div>
                {customer.lastLoginAt && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Last login</span>
                    <span className="text-slate-800 text-xs">{new Date(customer.lastLoginAt).toLocaleDateString("en-AU")}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Created</span>
                  <span className="text-slate-800 text-xs">{new Date(customer.createdAt).toLocaleDateString("en-AU")}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Marketing */}
          <Card>
            <CardContent className="pt-5 space-y-4">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Marketing</h3>
              <div className="space-y-3">
                {customer.source && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Source</span>
                    <span className="text-slate-800">{customer.source}</span>
                  </div>
                )}
                {customer.campaign && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Campaign</span>
                    <span className="text-slate-800">{customer.campaign}</span>
                  </div>
                )}
                {customer.channel && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Channel</span>
                    <span className="text-slate-800">{customer.channel}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Email opt-out</span>
                  <span className={customer.neverEmail ? "text-red-600" : "text-green-600"}>
                    {customer.neverEmail ? "Yes" : "No"}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">No marketing</span>
                  <span className={customer.dontSendMarketing ? "text-red-600" : "text-green-600"}>
                    {customer.dontSendMarketing ? "Yes" : "No"}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* IDs */}
          {(customer.crmId || customer.referenceId) && (
            <Card>
              <CardContent className="pt-5 space-y-4">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">References</h3>
                <div className="space-y-3">
                  {customer.crmId && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">CRM ID</span>
                      <code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{customer.crmId}</code>
                    </div>
                  )}
                  {customer.referenceId && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Reference</span>
                      <code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{customer.referenceId}</code>
                    </div>
                  )}
                  {customer.crmCreatedAt && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">CRM created</span>
                      <span className="text-slate-800 text-xs">{customer.crmCreatedAt}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Fact Finder Confirmation Dialog */}
      <Dialog open={isFFConfirmOpen} onOpenChange={setIsFFConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Fact Finder</DialogTitle>
            <DialogDescription>
              This will create a new fact finder form and send an invite email to <strong className="text-foreground">{customer.email}</strong> with a link to fill it out.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 space-y-1.5">
            <p className="flex items-center gap-2"><FiMail className="w-3.5 h-3.5 text-slate-400 shrink-0" /> An email invite will be sent immediately</p>
            <p className="flex items-center gap-2"><FiFileText className="w-3.5 h-3.5 text-slate-400 shrink-0" /> The customer can complete it at their own pace</p>
            <p className="flex items-center gap-2"><FiCheck className="w-3.5 h-3.5 text-slate-400 shrink-0" /> You'll be notified when they submit</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setIsFFConfirmOpen(false)} disabled={isCreatingFF}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleCreateFactFinder} disabled={isCreatingFF}>
              <FiSend className="w-3.5 h-3.5 mr-1.5" />
              {isCreatingFF ? "Sending..." : "Create & Send Invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Preview Dialog */}
      <Dialog open={previewEventIdx !== null} onOpenChange={() => setPreviewEventIdx(null)}>
        {previewEventIdx !== null && customer.upcomingEvents[previewEventIdx]?.automationContent && (
          <DialogContent className="max-w-3xl w-[95vw] p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col">
            <div className="bg-primary px-5 py-4 shrink-0">
              <DialogHeader className="gap-0">
                <DialogTitle className="font-serif text-base text-primary-foreground">
                  {customer.upcomingEvents[previewEventIdx].label} - Email Preview
                </DialogTitle>
                <DialogDescription className="text-primary-foreground/60 text-xs mt-1">
                  Preview for {customer.preferredName || customer.name?.split(" ")[0] || "customer"}
                </DialogDescription>
              </DialogHeader>
            </div>
            <div className="px-5 py-2.5 border-b bg-slate-50 shrink-0 text-xs">
              <div className="flex gap-2">
                <span className="text-slate-400 font-medium w-12">To</span>
                <span className="text-slate-700">{customer.email}</span>
              </div>
              <div className="flex gap-2 mt-1">
                <span className="text-slate-400 font-medium w-12">Subject</span>
                <span className="text-slate-800 font-medium">
                  {interpolateCustomerVars(customer.upcomingEvents[previewEventIdx].automationSubject || "", customer)}
                </span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {/* Email layout matching wrapEmail() / automations preview */}
              <div style={{ backgroundColor: EMAIL_BRAND.cream, padding: "40px 20px" }}>
                <div style={{ maxWidth: 600, width: "100%", margin: "0 auto", backgroundColor: EMAIL_BRAND.white, borderRadius: 8, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                  {/* Logo */}
                  <div style={{ textAlign: "center", padding: "28px 40px 20px 40px" }}>
                    <img src={EMAIL_BRAND.logoUrl} alt={EMAIL_BRAND.companyName} style={{ display: "block", width: 220, maxWidth: "100%", height: "auto", margin: "0 auto" }} />
                  </div>
                  {/* Accent line */}
                  <div style={{ padding: "0 40px" }}>
                    <div style={{ borderTop: `3px solid ${EMAIL_BRAND.primary}` }} />
                  </div>
                  {/* Body */}
                  <div
                    style={{ padding: "32px 40px 40px 40px", color: EMAIL_BRAND.text, fontSize: 16, lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{
                      __html: contentToHtml(interpolateCustomerVars(customer.upcomingEvents[previewEventIdx].automationContent!, customer)),
                    }}
                  />
                  {/* Divider */}
                  <div style={{ padding: "0 40px" }}>
                    <div style={{ borderTop: `1px solid ${EMAIL_BRAND.border}` }} />
                  </div>
                  {/* Footer */}
                  <div style={{ padding: "28px 40px", backgroundColor: EMAIL_BRAND.cream, textAlign: "center", fontSize: 13, color: EMAIL_BRAND.textLight, lineHeight: 1.6 }}>
                    <p style={{ margin: "0 0 6px 0" }}>
                      <strong style={{ color: EMAIL_BRAND.primary }}>{EMAIL_BRAND.companyName}</strong>
                    </p>
                    <p style={{ margin: "0 0 6px 0" }}>
                      Australian Credit Licence {EMAIL_BRAND.acl} | ABN {EMAIL_BRAND.abn}
                    </p>
                    <p style={{ margin: "0 0 12px 0" }}>
                      {EMAIL_BRAND.address}
                    </p>
                    <p style={{ margin: 0 }}>
                      <a href="#" style={{ color: EMAIL_BRAND.accent, textDecoration: "underline" }}>Manage Preferences</a>
                      &nbsp;&nbsp;|&nbsp;&nbsp;
                      <a href="#" style={{ color: EMAIL_BRAND.accent, textDecoration: "underline" }}>Unsubscribe</a>
                    </p>
                  </div>
                  {/* Copyright */}
                  <div style={{ textAlign: "center", padding: "12px 40px 20px 40px" }}>
                    <p style={{ margin: 0, fontSize: 11, color: EMAIL_BRAND.textLight }}>
                      &copy; {new Date().getFullYear()} {EMAIL_BRAND.companyName}. All rights reserved.
                    </p>
                    <p style={{ margin: "4px 0 0 0", fontSize: 11 }}>
                      <a href={EMAIL_BRAND.siteUrl} style={{ color: EMAIL_BRAND.textLight, textDecoration: "underline" }}>pathfinancial.com.au</a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-5 py-2.5 border-t bg-white shrink-0 flex items-center justify-between">
              <p className="text-[11px] text-slate-400">Test sends to your admin email with this customer's real data</p>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setPreviewEventIdx(null)}>
                  Close
                </Button>
                {customer.upcomingEvents[previewEventIdx].automationId && (
                  <Button
                    size="sm"
                    onClick={() => handleSendTestEmail(customer.upcomingEvents[previewEventIdx].automationId!)}
                    disabled={isSendingTest}
                  >
                    <FiSend className="w-3.5 h-3.5 mr-1.5" />
                    {isSendingTest ? "Sending..." : "Send Test"}
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>

      {/* Add Loan Dialog */}
      <Dialog open={isAddLoanOpen} onOpenChange={setIsAddLoanOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Loan</DialogTitle>
            <DialogDescription>Add a new loan record for {customer.preferredName || customer.name?.split(" ")[0] || "this customer"}.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2">
              <label className="text-xs font-medium text-slate-600 mb-1 block">Purpose</label>
              <Input
                placeholder="e.g. Purchase Existing-OO"
                value={loanForm.loanPurpose}
                onChange={(e) => setLoanForm({ ...loanForm, loanPurpose: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Category</label>
              <Input
                placeholder="e.g. Residential Mortgage"
                value={loanForm.loanCategory}
                onChange={(e) => setLoanForm({ ...loanForm, loanCategory: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Loan Type</label>
              <Select value={loanForm.loanType} onValueChange={(v) => setLoanForm({ ...loanForm, loanType: v })}>
                <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="home_purchase">Home Purchase</SelectItem>
                  <SelectItem value="refinance">Refinance</SelectItem>
                  <SelectItem value="investment">Investment</SelectItem>
                  <SelectItem value="first_home">First Home</SelectItem>
                  <SelectItem value="construction">Construction</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Amount</label>
              <Input
                type="number"
                placeholder="450000"
                value={loanForm.loanAmount}
                onChange={(e) => setLoanForm({ ...loanForm, loanAmount: e.target.value })}
              />
            </div>
            <div className="relative" ref={lenderDropdownRef}>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Lender</label>
              <button
                type="button"
                onClick={() => { setIsLenderDropdownOpen(!isLenderDropdownOpen); setLenderSearch(""); }}
                className="flex items-center gap-2 w-full h-11 rounded-md border bg-transparent px-3 py-2 text-sm text-left"
              >
                {loanForm.lender ? (
                  <>
                    {getLenderLogo(loanForm.lender) && (
                      <img src={getLenderLogo(loanForm.lender)!} alt="" className="w-5 h-5 object-contain shrink-0" />
                    )}
                    <span className="truncate">{loanForm.lender}</span>
                  </>
                ) : (
                  <span className="text-muted-foreground">Select lender...</span>
                )}
              </button>
              {isLenderDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white rounded-lg border shadow-lg overflow-hidden">
                  <div className="p-2 border-b">
                    <div className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 rounded-md">
                      <FiSearch className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <input
                        autoFocus
                        type="text"
                        placeholder="Search lenders..."
                        value={lenderSearch}
                        onChange={(e) => setLenderSearch(e.target.value)}
                        className="bg-transparent text-sm outline-none w-full"
                      />
                    </div>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto">
                    {(() => {
                      const allLenders = getAllLenderLogos();
                      const seen = new Set<string>();
                      const unique = allLenders.filter(l => {
                        if (seen.has(l.logo)) return false;
                        seen.add(l.logo);
                        return true;
                      });
                      const filtered = unique.filter(l =>
                        l.name.toLowerCase().includes(lenderSearch.toLowerCase())
                      );
                      return (
                        <>
                          {filtered.map(l => (
                            <button
                              key={l.name}
                              type="button"
                              onClick={() => {
                                setLoanForm({ ...loanForm, lender: l.name });
                                setIsLenderDropdownOpen(false);
                              }}
                              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm hover:bg-slate-50 transition-colors text-left"
                            >
                              <img src={l.logo} alt="" className="w-5 h-5 object-contain shrink-0" />
                              <span className="truncate">{l.name}</span>
                            </button>
                          ))}
                          {lenderSearch.trim() && !filtered.some(l => l.name.toLowerCase() === lenderSearch.toLowerCase()) && (
                            <button
                              type="button"
                              onClick={() => {
                                setLoanForm({ ...loanForm, lender: lenderSearch.trim() });
                                setIsLenderDropdownOpen(false);
                              }}
                              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm hover:bg-slate-50 transition-colors text-left border-t text-accent"
                            >
                              <FiPlus className="w-4 h-4 shrink-0" />
                              <span>Use "{lenderSearch.trim()}"</span>
                            </button>
                          )}
                          {filtered.length === 0 && !lenderSearch.trim() && (
                            <p className="text-xs text-slate-400 text-center py-3">No lenders found</p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-slate-600 mb-1 block">Settlement Date</label>
              <Input
                type="date"
                value={loanForm.settlementDate}
                onChange={(e) => setLoanForm({ ...loanForm, settlementDate: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setIsAddLoanOpen(false)} disabled={isAddingLoan}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleAddLoan} disabled={isAddingLoan}>
              {isAddingLoan ? "Adding..." : "Add Loan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Loan Dialog */}
      <Dialog open={deleteLoanId !== null} onOpenChange={() => setDeleteLoanId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Loan</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this loan record? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setDeleteLoanId(null)} disabled={isDeletingLoan}>
              Cancel
            </Button>
            <Button size="sm" variant="destructive" onClick={handleDeleteLoan} disabled={isDeletingLoan}>
              <FiTrash2 className="w-3.5 h-3.5 mr-1.5" />
              {isDeletingLoan ? "Deleting..." : "Delete Loan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
